// src/pages/Level-3.jsx
// ✅ V2: Free trial completion modal — shown after Level 3 complete
//        This is the highest-intent conversion moment: the child just finished
//        all 3 free songs. Modal shows what they unlock with premium (₹299).

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LevelTemplate from "./LevelTemplate";
import Confetti from "react-confetti";

import { STREAM_VIDEOS } from "../videos/streamVideos";
import { TrophyStorage } from "../services/trophyStorage";
import { sendGAEvent } from "../analytics/analytics";
import { usePremiumStatus } from "../hooks/usePremiumStatus";
import PianoLoadingScreen from "../components/PianoLoadingScreen";

/* ─────────────────────────────────────────────
   CONFIG – Level 3: Ode to Joy (Peng)
   ───────────────────────────────────────────── */

const LEVEL3_FALLBACK_STREAM_ID = "3089ef96311a65e8601c96b4692d89d9";
const LEVEL3_STREAM_ID =
  STREAM_VIDEOS?.L3_PENG_INTRO?.id || LEVEL3_FALLBACK_STREAM_ID;
const INTRO_VIDEO_SRC = LEVEL3_STREAM_ID ? `stream:${LEVEL3_STREAM_ID}` : null;
const PENG_HERO_IMG = "/images/Peng-Hero.png";

const LEVEL3_MELODY_LINES = [
  { id: 0, notes: ["E", "E", "F", "G"], lyric: "Penguin Don't Cry" },
  { id: 1, notes: ["G", "F", "E", "D"], lyric: "Penguin Let's Smile" },
  { id: 2, notes: ["C", "C", "D", "E"], lyric: "Laugh And Play" },
  { id: 3, notes: ["E", "D", "D"],      lyric: "Filled With Joy" },
  { id: 4, notes: ["E", "E", "F", "G"], lyric: "Penguin Don't Cry" },
  { id: 5, notes: ["G", "F", "E", "D"], lyric: "Penguin Let's Smile" },
  { id: 6, notes: ["C", "C", "D", "E"], lyric: "Laugh And Play" },
  { id: 7, notes: ["D", "C", "C"],      lyric: "Filled With Joy" },
];

const LEVEL3_LYRIC_GROUPS = [
  ["Penguin", "Don't",  "Cry"],
  ["Penguin", "Let's",  "Smile"],
  ["Laugh",   "And",    "Play"],
  ["Filled",  "With",   "Joy"],
  ["Penguin", "Don't",  "Cry"],
  ["Penguin", "Let's",  "Smile"],
  ["Laugh",   "And",    "Play"],
  ["Filled",  "With",   "Joy"],
];

const LEVEL3_NOTE_GROUP_INDEXES = [
  [{ start: 0, end: 1 }, { start: 2, end: 2 }, { start: 3, end: 3 }],
  [{ start: 0, end: 1 }, { start: 2, end: 2 }, { start: 3, end: 3 }],
  [{ start: 0, end: 1 }, { start: 2, end: 2 }, { start: 3, end: 3 }],
  [{ start: 0, end: 0 }, { start: 1, end: 1 }, { start: 2, end: 2 }],
  [{ start: 0, end: 1 }, { start: 2, end: 2 }, { start: 3, end: 3 }],
  [{ start: 0, end: 1 }, { start: 2, end: 2 }, { start: 3, end: 3 }],
  [{ start: 0, end: 1 }, { start: 2, end: 2 }, { start: 3, end: 3 }],
  [{ start: 0, end: 0 }, { start: 1, end: 1 }, { start: 2, end: 2 }],
];

/* ─────────────────────────────────────────────
   FREE TRIAL COMPLETE MODAL
   Shown after Level 3 completion — highest-intent upsell moment.
   ───────────────────────────────────────────── */

const PREMIUM_LEVELS = [
  { id: 4,  emoji: "🔥", title: "Doron Awakens",      song: "Machli Jal Ki Rani"           },
  { id: 5,  emoji: "🦊", title: "Ren's Network",       song: "Jingle Bells"                 },
  { id: 6,  emoji: "🕷️", title: "The Spider Dance",    song: "Incey Wincey Spider"          },
  { id: 7,  emoji: "🐼", title: "Panda Password",      song: "London Bridge Is Falling Down"},
  { id: 8,  emoji: "🦚", title: "Peacock's Pride",     song: "The National Anthem"          },
  { id: 9,  emoji: "🐊", title: "Crocodile Test",      song: "Row Row Row Your Boat"        },
  { id: 10, emoji: "🏛️", title: "The Ancient Melody",  song: "Ancient Melody (Final Boss)"  },
];

function FreeTrialCompleteModal({ open, onClose, onGetPremium }) {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 800,
    height: typeof window !== "undefined" ? window.innerHeight : 600,
  });

  useEffect(() => {
    if (!open) return;
    const fn = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [open]);

  // ✅ GA: track modal view
  useEffect(() => {
    if (!open) return;
    sendGAEvent("free_trial_complete_modal_view", { level: 3 });
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/70 p-3 sm:p-4"
      style={{ backdropFilter: "blur(8px)" }}
    >
      {/* Confetti behind the modal */}
      <Confetti
        width={windowSize.width}
        height={windowSize.height}
        numberOfPieces={320}
        recycle={false}
        gravity={0.14}
        style={{ zIndex: 10002, pointerEvents: "none" }}
      />

      <div
        className="relative z-[10003] w-full max-w-md bg-white rounded-[32px] shadow-2xl overflow-hidden"
        style={{ animation: "ftcPop 0.45s cubic-bezier(0.34,1.56,0.64,1)" }}
      >
        <style>{`
          @keyframes ftcPop {
            from { opacity: 0; transform: scale(0.82) translateY(24px); }
            to   { opacity: 1; transform: scale(1) translateY(0); }
          }
          @keyframes ftcShine {
            0%   { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
          .ftc-shine {
            background: linear-gradient(90deg, #7c3aed, #a855f7, #7c3aed);
            background-size: 200% auto;
            animation: ftcShine 2.4s linear infinite;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }
        `}</style>

        {/* Close button */}
        <button
          type="button"
          onClick={() => {
            sendGAEvent("free_trial_complete_modal_dismissed");
            onClose();
          }}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 font-bold flex items-center justify-center text-sm transition"
        >
          ✕
        </button>

        {/* ── Header gradient band ── */}
        <div className="bg-gradient-to-r from-[#7c3aed] to-[#6d28d9] px-5 pt-6 pb-5 text-center">
          <div className="text-4xl mb-2">🎉</div>
          <h2
            className="text-xl sm:text-2xl font-black text-white leading-tight"
            style={{ fontFamily: "'Fredoka One', cursive" }}
          >
            You finished all 3 free songs!
          </h2>
          <p className="mt-1.5 text-sm font-semibold text-purple-200">
            Twinkle Twinkle · Happy Birthday · Ode to Joy — all done!
          </p>

          {/* Stars earned */}
          <div className="mt-3 flex items-center justify-center gap-2">
            {["🐉", "🦓", "🐧"].map((emoji, i) => (
              <div
                key={i}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center text-xl sm:text-2xl"
              >
                {emoji}
              </div>
            ))}
          </div>
          <div className="mt-1.5 text-xs font-bold text-purple-200">3 stars collected ⭐⭐⭐</div>
        </div>

        {/* ── Body ── */}
        <div className="px-5 py-4 max-h-[55vh] overflow-y-auto">
          <p className="text-center text-sm font-bold text-slate-600 mb-3">
            Unlock <span className="text-[#7c3aed] font-black">7 more levels</span> to defeat all the bosses and complete the quest!
          </p>

          {/* Premium level list */}
          <div className="flex flex-col gap-2 mb-4">
            {PREMIUM_LEVELS.map((level) => (
              <div
                key={level.id}
                className="flex items-center gap-3 px-3 py-2.5 rounded-2xl border border-purple-100 bg-gradient-to-r from-purple-50 to-white"
              >
                <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-lg flex-shrink-0">
                  {level.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className="text-sm font-black text-slate-800 truncate"
                    style={{ fontFamily: "'Fredoka One', cursive" }}
                  >
                    Level {level.id} · {level.title}
                  </div>
                  <div className="text-[11px] font-semibold text-purple-600 truncate">
                    🎵 {level.song}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <span className="text-[10px] font-black bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                    LOCKED
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Price + CTA */}
          <div className="text-center mb-3">
            <div className="text-xs font-semibold text-slate-500 mb-0.5 line-through">₹499</div>
            <div
              className="text-3xl sm:text-4xl font-black text-[#7c3aed] mb-0.5"
              style={{ fontFamily: "'Fredoka One', cursive" }}
            >
              ₹299
            </div>
            <div className="text-xs font-bold text-emerald-600 mb-3">
              ✅ 40% off launch offer · Unlimited access
            </div>

            <button
              type="button"
              onClick={() => {
                sendGAEvent("free_trial_complete_modal_cta_clicked", {
                  from_level: 3,
                  destination: "paywall",
                });
                onGetPremium();
              }}
              className="w-full py-3.5 px-6 rounded-full text-white font-black text-base sm:text-lg shadow-[0_8px_0_#5b21b6] hover:brightness-105 active:translate-y-[2px] active:shadow-[0_4px_0_#5b21b6] transition"
              style={{
                background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                fontFamily: "'Fredoka One', cursive",
              }}
            >
              🔓 Unlock Full Game — ₹299
            </button>
          </div>

          {/* Secondary: back to game menu */}
          <button
            type="button"
            onClick={() => {
              sendGAEvent("free_trial_complete_modal_dismissed", { action: "back_to_menu" });
              onClose();
            }}
            className="w-full text-sm font-bold text-slate-500 hover:text-slate-700 transition py-1"
          >
            Maybe later — back to menu
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   HERO COMPONENT – "Peng at Treb Lake"
   ───────────────────────────────────────────── */

function PengLakeHero({ noteEvent, isComplete }) {
  const [shakeMode, setShakeMode] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);
  const [pops, setPops] = useState([]);
  const [imageOk, setImageOk] = useState(true);

  useEffect(() => {
    if (noteEvent !== "miss") return;
    setShakeMode("hard");
    const t1 = setTimeout(() => setShakeMode(""), 360);
    const id = Date.now() + Math.random();
    const EMOJIS = ["😬", "❌", "🙈", "😅"];
    const emoji = EMOJIS[Math.floor(Math.random() * EMOJIS.length)];
    const x = 18 + Math.random() * 64;
    const y = 22 + Math.random() * 52;
    setPops((prev) => [...prev, { id, emoji, x, y, kind: "miss" }]);
    const t2 = setTimeout(() => { setPops((prev) => prev.filter((p) => p.id !== id)); }, 760);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [noteEvent]);

  useEffect(() => {
    if (noteEvent !== "hit") return;
    const id = Date.now() + Math.random();
    const HAPPY = ["😄", "😊", "😁", "🙂"];
    const emoji = HAPPY[Math.floor(Math.random() * HAPPY.length)];
    const x = 22 + Math.random() * 56;
    const y = 26 + Math.random() * 46;
    setPops((prev) => [...prev, { id, emoji, x, y, kind: "hit" }]);
    const t = setTimeout(() => { setPops((prev) => prev.filter((p) => p.id !== id)); }, 720);
    if (Math.random() < 0.35) {
      const id2 = Date.now() + Math.random();
      const EXTRA = ["✨", "🎶", "⭐️", "❄️"];
      const emoji2 = EXTRA[Math.floor(Math.random() * EXTRA.length)];
      const x2 = 18 + Math.random() * 64;
      const y2 = 22 + Math.random() * 52;
      setPops((prev) => [...prev, { id: id2, emoji: emoji2, x: x2, y: y2, kind: "hit2" }]);
      const t2 = setTimeout(() => { setPops((prev) => prev.filter((p) => p.id !== id2)); }, 820);
      return () => { clearTimeout(t); clearTimeout(t2); };
    }
    return () => clearTimeout(t);
  }, [noteEvent]);

  useEffect(() => {
    if (!isComplete) return;
    setShowConfetti(true);
    const t = setTimeout(() => setShowConfetti(false), 2800);
    return () => clearTimeout(t);
  }, [isComplete]);

  return (
    <div className="relative w-full max-w-4xl mx-auto mt-2">
      <style>{`
        @keyframes tt-shake-hard {
          0%   { transform: translate(0,0) scale(1); }
          15%  { transform: translate(-6px, 2px) scale(0.992); }
          30%  { transform: translate(6px, -2px) scale(0.988); }
          45%  { transform: translate(-5px, -3px) scale(0.992); }
          60%  { transform: translate(5px, 3px) scale(0.988); }
          75%  { transform: translate(-3px, 1px) scale(0.994); }
          100% { transform: translate(0,0) scale(1); }
        }
        @keyframes tt-pop-up {
          0%   { opacity: 0; transform: translate(-50%,-50%) scale(0.6); }
          15%  { opacity: 1; transform: translate(-50%,-58%) scale(1.05); }
          100% { opacity: 0; transform: translate(-50%,-90%) scale(0.95); }
        }
      `}</style>

      {showConfetti && typeof window !== "undefined" && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={160}
          recycle={false}
          gravity={0.16}
        />
      )}

      <div className="relative rounded-[22px] overflow-hidden shadow-md border border-white/70 bg-gradient-to-b from-sky-100 to-indigo-50">
        <div
          className="relative"
          style={{
            animation: shakeMode === "hard" ? "tt-shake-hard 0.36s ease-out" : "none",
            filter: shakeMode === "hard" ? "saturate(0.95)" : "none",
          }}
        >
          {imageOk ? (
            <img
              src={PENG_HERO_IMG}
              alt="Peng at Treb Lake"
              className="w-full h-[150px] md:h-[190px] object-cover"
              onError={() => setImageOk(false)}
            />
          ) : (
            <div className="w-full h-[150px] md:h-[190px] grid place-items-center">
              <div className="text-center px-4">
                <div className="text-3xl">🐧❄️</div>
                <div className="text-sm font-extrabold text-slate-800 mt-1">
                  Treb Lake · Ode to Joy
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="pointer-events-none absolute inset-0">
          {pops.map((p) => (
            <div
              key={p.id}
              className="absolute"
              style={{ left: `${p.x}%`, top: `${p.y}%`, animation: "tt-pop-up 0.72s ease-out forwards" }}
            >
              <div className="select-none text-3xl sm:text-4xl">{p.emoji}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
   ───────────────────────────────────────────── */

export default function Level3() {
  const navigate = useNavigate();
  const { isPremium, premiumLoaded } = usePremiumStatus();

  // ✅ V2: Show the upsell modal after level completion
  const [showFreeTrialModal, setShowFreeTrialModal] = useState(false);

  // ✅ V3: If the user taps "Next Level" before the premium check has
  // resolved, we show the existing loading screen instead of guessing —
  // then route correctly the instant premiumLoaded flips true.
  const [awaitingPremiumCheck, setAwaitingPremiumCheck] = useState(false);

  const handleComplete = () => {
    TrophyStorage.markLevelAndStar(3);

    // GA: level 3 complete = end of free trial
    sendGAEvent("level_complete", {
      level_number: 3,
      level_name: "Level 3",
      is_final_free_level: true,
    });

    // Show the celebration + upsell modal after a short delay
    // (LevelTemplate's own star collection animation plays first)
    setTimeout(() => {
      setShowFreeTrialModal(true);
    }, 3200);
  };

  // ✅ V3: "Next Level" now checks premium status instead of always
  // sending everyone to the paywall. Premium users go straight to
  // /level-4; free users still see the upsell paywall.
  const handleNextLevel = () => {
    if (!premiumLoaded) {
      // Premium check hasn't resolved yet — wait for it (see effect below)
      // rather than guessing and possibly sending a paying user to the
      // paywall by mistake.
      setAwaitingPremiumCheck(true);
      return;
    }

    sendGAEvent("level3_next_level_click", { is_premium: isPremium });

    if (isPremium) {
      navigate("/level-4");
    } else {
      navigate("/pwp-paywall?reason=premium_required&next=/level-4");
    }
  };

  // Once the premium check resolves (if the user was waiting on it),
  // route them immediately — no extra click needed.
  useEffect(() => {
    if (!awaitingPremiumCheck || !premiumLoaded) return;

    sendGAEvent("level3_next_level_click", {
      is_premium: isPremium,
      waited_for_premium_check: true,
    });

    if (isPremium) {
      navigate("/level-4");
    } else {
      navigate("/pwp-paywall?reason=premium_required&next=/level-4");
    }
    // No need to reset awaitingPremiumCheck — navigate() unmounts this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingPremiumCheck, premiumLoaded, isPremium]);

  // ✅ Same pattern GameMenu.jsx / PwpPaywall.jsx already use elsewhere —
  // reuse the existing branded loading screen instead of a blank flash.
  if (awaitingPremiumCheck) {
    return <PianoLoadingScreen message="Checking your pass" />;
  }

  return (
    <>
      <LevelTemplate
        levelId={3}
        videoSrc={INTRO_VIDEO_SRC}
        startLabel="Start Game"
        melodyLines={LEVEL3_MELODY_LINES}
        lyricGroupsPerLine={LEVEL3_LYRIC_GROUPS}
        noteGroupIndexesPerLine={LEVEL3_NOTE_GROUP_INDEXES}
        backgroundImage={PENG_HERO_IMG}
        heroImageFit="cover"
        renderHero={(heroProps) => <PengLakeHero {...heroProps} />}
        currentLevelForSidebar={3}
        completionTitle="Ode to Joy Complete! 🐧⭐"
        completionBody={
          <>
            You played{" "}
            <span className="font-semibold text-[#4F46E5]">Ode to Joy</span> — Peng
            is super happy! You've completed all 3 free songs.{" "}
            <span className="font-semibold text-[#7C3AED]">Ready for the full quest?</span>
          </>
        }
        onComplete={handleComplete}
        onNextLevel={handleNextLevel}
      />

      {/* ✅ Free trial completion upsell modal — this is a deliberate
          "Get Premium" CTA, not a navigation bug, so it always goes to
          the paywall on purpose (even for premium users, since they'd
          already be routed to /level-4 by handleNextLevel above and
          would never see this modal fire in practice). */}
      <FreeTrialCompleteModal
        open={showFreeTrialModal}
        onClose={() => setShowFreeTrialModal(false)}
        onGetPremium={() => {
          setShowFreeTrialModal(false);
          navigate("/pwp-paywall?reason=free_trial_complete&next=/level-4");
        }}
      />
    </>
  );
}